import { IComplemento } from "../../produtos/interfaces/IComplemento";
import { PrecificacaoComplemento } from "../../produtos/enum/PrecificacaoComplemento";
import { TipoItemProduto } from "../../produtos/enum/TipoItemProduto";
import { IItemProduto } from "../interface/IItemProduto";

/**
 * Calcula o valor total que um grupo de complementos adiciona ao produto,
 * de acordo com sua regra de precificação:
 * - soma (padrão): Σ(preço × quantidade) de cada item selecionado
 * - precoDividido: média dos itens selecionados (Σ(preço×qtd) / Σqtd, arredondado p/ cima) — valor único
 * - maiorPreco: apenas o item mais caro conta — valor único
 *
 * Espelha a lógica de precificação de pizza em calcularValorTotalProduto.ts.
 */
export const calcularPrecoComplemento = (complemento: IComplemento): number => {
    const itens = complemento.itensSelecionados ?? [];
    if (!itens.length) {
        return 0;
    }

    const precificacao = complemento.precificacao ?? PrecificacaoComplemento.Somar;

    if (precificacao === PrecificacaoComplemento["Preço dividido"]) {
        const somaPrecos = itens.reduce((soma, item) => soma + (item.preco ?? 0) * (item.quantidade ?? 1), 0);
        const totalUnidades = itens.reduce((soma, item) => soma + (item.quantidade ?? 1), 0);
        return totalUnidades > 0 ? Math.ceil(somaPrecos / totalUnidades) : 0;
    }

    if (precificacao === PrecificacaoComplemento["Maior preço"]) {
        return itens.reduce((maior, item) => Math.max(maior, item.preco ?? 0), 0);
    }

    // soma (padrão)
    return itens.reduce((soma, item) => soma + (item.preco ?? 0) * (item.quantidade ?? 1), 0);
};

/**
 * Achata os itens selecionados de um grupo de complementos em linhas de pedido (IItemProduto),
 * já ajustando os preços conforme a regra de precificação. A soma das linhas geradas é sempre
 * igual a calcularPrecoComplemento(complemento), de modo que toda a soma downstream
 * (carrinho/pedido/backend) reflita automaticamente o modo escolhido.
 *
 * Para modos diferentes de "soma", mantém-se uma linha por item (descrição/KDS), mas o valor
 * único do grupo é atribuído à primeira linha e as demais recebem preço 0.
 */
export const tratarItensComplemento = (complemento: IComplemento): IItemProduto[] => {
    const itensSelecionados = complemento.itensSelecionados ?? [];
    if (!itensSelecionados.length) {
        return [];
    }

    const precificacao = complemento.precificacao ?? PrecificacaoComplemento.Somar;

    if (precificacao === PrecificacaoComplemento.Somar) {
        return itensSelecionados.map(item => ({
            descricao: item.descricao,
            preco: item.preco,
            quantidade: item.quantidade ?? 1,
            tipo: TipoItemProduto.item,
            uuidReferencia: item.uuid
        }));
    }

    const valorGrupo = calcularPrecoComplemento(complemento);
    return itensSelecionados.map((item, index) => ({
        descricao: item.descricao,
        preco: index === 0 ? valorGrupo : 0,
        quantidade: 1,
        tipo: TipoItemProduto.item,
        uuidReferencia: item.uuid
    }));
};
